import {
    ChatBubbleLeftIcon,
    EnvelopeIcon,
    InboxIcon,
    PaperClipIcon,
    PhoneIcon,
} from "@heroicons/react/24/outline";
import { EmailEntityId, formatDateNoTime, formatPhoneNumber, UserId } from "@sp-crm/core";
import { ButtonSelect, ButtonSelectOption } from "components/ui/button-select";
import { ClientLink, CommunityLink } from "components/ui/entity-link";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { ResponsiveMode } from "store/reducers/responsive";
import {
    useCurrentUserId,
    useFeature,
    useResponsiveMode,
    useUsers,
} from "store/selectors/hooks";
import {
    AdvancedSearchEntityType,
    ContactSearchHitFragmentFragment,
    ContactSearchHitType,
    ConversationFeedBasicsFragment,
    ConversationThreadType,
    VoiceCallDirection,
} from "../../generated/graphql";
import { formatCallDuration } from "./call-duration";
import {
    EmailEntityRef,
    emailEntityRefFromHit,
    getChannelFilterCounts,
    getEntityDisplayName,
    MessageChannelFilter,
    pickEmailThreadPreview,
    selectedThreadRowClass,
    threadMatchesChannelFilter,
    threadRowClass,
} from "./conversation-feed-utils";
import { EmailThreadComponentInline } from "./email-thread-component-inline";
import {
    LeadSourceEmailListItem,
    LeadSourceEmailReadPane,
    leadSourceEmailSubject,
} from "./lead-source-email-thread";
import { SmsMessageThreadInline } from "./sms-message-thread-inline";
import {
    VoiceCallDetailInline,
    voiceCallStatusPreview,
} from "./voice-call-detail-inline";

const BOTTOM_GAP_PX = 16;

export type ConversationFeedMode =
    | { kind: "entity"; entityId: EmailEntityId; entityType: AdvancedSearchEntityType }
    | { kind: "inbox" };

export interface ConversationFeedProps {
    feed: ConversationFeedBasicsFragment;
    initialThreadId?: string;
    mode: ConversationFeedMode;
}

type Thread = ConversationFeedBasicsFragment["threads"][number];

const entityIdFromMode = (mode: ConversationFeedMode): EmailEntityId | undefined =>
    mode.kind === "entity" ? mode.entityId : undefined;

const entityTypeFromMode = (
    mode: ConversationFeedMode,
): AdvancedSearchEntityType | undefined =>
    mode.kind === "entity" ? mode.entityType : undefined;

export const ConversationFeed: React.FC<ConversationFeedProps> = props => {
    const { feed, initialThreadId, mode } = props;
    const [selectedThreadId, setSelectedThreadId] = useState<string | null>(
        initialThreadId ?? null,
    );
    const responsiveMode = useResponsiveMode();
    const isMobile = responsiveMode < ResponsiveMode.large;

    const showChannelPills = mode.kind === "inbox";
    const voicePillEnabled = useFeature("callTranscription");
    const [channelFilter, setChannelFilter] = useState<MessageChannelFilter>("all");

    const changeChannelFilter = (filter: MessageChannelFilter) => {
        if (filter === channelFilter) {
            return;
        }
        setChannelFilter(filter);
        setSelectedThreadId(null);
    };

    const sortedThreads = useMemo(
        () =>
            [...feed.threads].sort(
                (a, b) =>
                    new Date(b.latestTimestamp).getTime() -
                    new Date(a.latestTimestamp).getTime(),
            ),
        [feed.threads],
    );

    const filteredThreads = useMemo(
        () =>
            sortedThreads.filter(thread =>
                threadMatchesChannelFilter(thread.type, channelFilter),
            ),
        [sortedThreads, channelFilter],
    );

    const channelCounts = useMemo(
        () => getChannelFilterCounts(sortedThreads),
        [sortedThreads],
    );

    const selectedThread = sortedThreads.find(t => t.id === selectedThreadId) ?? null;

    const containerRef = useRef<HTMLDivElement>(null);
    useViewportFitHeight(containerRef);

    if (sortedThreads.length === 0) {
        return (
            <div className="flex flex-col items-center text-center py-12 gap-3 text-gray-400">
                <InboxIcon className="h-10 w-10" aria-hidden="true" />
                <p className="text-sm">No messages yet</p>
            </div>
        );
    }

    if (isMobile) {
        return (
            <>
                <div className="border border-gray-200 rounded-sm overflow-hidden bg-white">
                    {showChannelPills ? (
                        <div className="flex-none px-3 pt-2 border-b border-gray-100">
                            <ChannelFilterPills
                                active={channelFilter}
                                onChange={changeChannelFilter}
                                showVoice={voicePillEnabled}
                                counts={channelCounts}
                            />
                        </div>
                    ) : null}
                    {filteredThreads.length === 0 ? (
                        <ChannelFilterEmptyState filter={channelFilter} />
                    ) : (
                        <ul role="list" className="divide-y divide-gray-100">
                            {filteredThreads.map(thread => (
                                <ThreadListItem
                                    key={thread.id}
                                    thread={thread}
                                    selected={false}
                                    mode={mode}
                                    onSelect={() => setSelectedThreadId(thread.id)}
                                />
                            ))}
                        </ul>
                    )}
                </div>
                <ResponsivePanel
                    isOpen={selectedThread !== null}
                    type={PanelType.extraLarge}
                    mobileVariant="fullscreen"
                    bodyLayout="fill"
                    noPadding={true}
                    headerText={
                        selectedThread
                            ? getThreadHeaderText(
                                  selectedThread,
                                  mode.kind === "inbox"
                                      ? (pickMostRelevantHitWithName(
                                            selectedThread.contactSearchHits,
                                        )?.name ?? null)
                                      : null,
                              )
                            : ""
                    }
                    onDismiss={() => setSelectedThreadId(null)}>
                    {selectedThread ? (
                        <ThreadReadPane
                            thread={selectedThread}
                            mode={mode}
                            ownerBadgePlacement="header"
                        />
                    ) : null}
                </ResponsivePanel>
            </>
        );
    }

    const desktopPanes = (
        <>
            <div className="w-80 flex-shrink-0 border-r border-gray-200 flex flex-col min-h-0">
                {filteredThreads.length === 0 ? (
                    <ChannelFilterEmptyState filter={channelFilter} />
                ) : (
                    <ul
                        role="list"
                        className="divide-y divide-gray-100 overflow-y-auto min-h-0">
                        {filteredThreads.map(thread => (
                            <ThreadListItem
                                key={thread.id}
                                thread={thread}
                                selected={thread.id === selectedThreadId}
                                mode={mode}
                                onSelect={() => setSelectedThreadId(thread.id)}
                            />
                        ))}
                    </ul>
                )}
            </div>
            <div className="flex-1 min-w-0 overflow-hidden flex flex-col">
                {selectedThread ? (
                    <ThreadReadPane
                        thread={selectedThread}
                        mode={mode}
                        ownerBadgePlacement={mode.kind === "inbox" ? "toolbar" : "header"}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
                        <InboxIcon className="h-10 w-10" aria-hidden="true" />
                        <p className="text-sm">
                            {readPanePlaceholderMessage[channelFilter]}
                        </p>
                    </div>
                )}
            </div>
        </>
    );

    if (showChannelPills) {
        return (
            <div
                ref={containerRef}
                className="sticky top-0 flex flex-col border border-gray-200 rounded-sm overflow-hidden bg-white">
                <div className="flex-none flex items-center justify-between gap-3 border-b border-gray-200 px-3">
                    <div className="min-w-0">
                        <ChannelFilterPills
                            active={channelFilter}
                            onChange={changeChannelFilter}
                            showVoice={voicePillEnabled}
                            counts={channelCounts}
                        />
                    </div>
                    <ViaBadge ownerUserId={selectedThread?.ownerUserId} />
                </div>
                <div className="flex flex-1 min-h-0">{desktopPanes}</div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="sticky top-0 flex border border-gray-200 rounded-sm overflow-hidden bg-white">
            {desktopPanes}
        </div>
    );
};

function getThreadHeaderText(thread: Thread, namedHitName: string | null): string {
    if (
        thread.type === ConversationThreadType.LeadSourceEmail &&
        thread.leadSourceEmail
    ) {
        return leadSourceEmailSubject(thread.leadSourceEmail);
    }
    if (thread.type === ConversationThreadType.Sms) {
        const messages = thread.smsThread?.messages ?? [];
        const latest = messages[0];
        if (!latest) {
            return "Conversation";
        }
        const phoneNumber = latest.direction === "Inbound" ? latest.from : latest.to;
        return namedHitName ?? formatPhoneNumber(phoneNumber);
    }
    if (thread.type === ConversationThreadType.EmailThread) {
        const emailThread = thread.emailThread;
        const sentMessages = emailThread?.sentMessages ?? [];
        const mailboxMessages = emailThread?.mailboxMessages ?? [];
        const oldestSent = sentMessages[sentMessages.length - 1];
        const oldestReceived = mailboxMessages[mailboxMessages.length - 1];
        return oldestSent?.subject ?? oldestReceived?.subject ?? "(no subject)";
    }
    if (thread.type === ConversationThreadType.VoiceCall && thread.voiceCall) {
        const remoteNumber =
            thread.voiceCall.direction === VoiceCallDirection.Inbound
                ? thread.voiceCall.from
                : thread.voiceCall.to;
        return namedHitName ?? formatPhoneNumber(remoteNumber);
    }
    return "Conversation";
}

interface ThreadListItemProps {
    thread: Thread;
    selected: boolean;
    mode: ConversationFeedMode;
    onSelect: () => void;
}

const ThreadListItem: React.FC<ThreadListItemProps> = props => {
    const { thread, selected, mode, onSelect } = props;
    const rowClass = selected ? selectedThreadRowClass : threadRowClass;

    if (
        thread.type === ConversationThreadType.LeadSourceEmail &&
        thread.leadSourceEmail
    ) {
        return (
            <LeadSourceEmailListItem
                email={thread.leadSourceEmail}
                selected={selected}
                onSelect={onSelect}
            />
        );
    }

    if (thread.type === ConversationThreadType.Sms) {
        const messages = thread.smsThread?.messages ?? [];
        const latest = messages[0];
        if (!latest) return null;
        const isInbound = latest.direction === "Inbound";
        const phoneNumber = isInbound ? latest.from : latest.to;
        const showEntity = mode.kind === "inbox";
        const hitWithName = showEntity
            ? pickMostRelevantHitWithName(thread.contactSearchHits)
            : null;
        const titleText = hitWithName?.name ?? formatPhoneNumber(phoneNumber);
        return (
            <li className={rowClass} onClick={onSelect}>
                <ChatBubbleLeftIcon
                    className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-1 ${
                        isInbound ? "transform scale-x-[-1]" : ""
                    }`}
                    aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm text-gray-900 truncate">
                            {titleText}
                        </span>
                        <span className="text-[11px] tabular-nums text-gray-400 ml-auto flex-shrink-0">
                            {formatDateNoTime(latest.dateAdded)}
                        </span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">{latest.body}</div>
                    {hitWithName ? (
                        <div className="mt-1.5 flex items-center gap-2 min-w-0">
                            <EntityPill type={hitWithName.hit.type} />
                            <span className="text-[11px] text-gray-400 truncate">
                                {formatPhoneNumber(phoneNumber)}
                            </span>
                        </div>
                    ) : null}
                </div>
            </li>
        );
    }

    if (thread.type === ConversationThreadType.EmailThread) {
        const emailThread = thread.emailThread;
        const sentMessages = emailThread?.sentMessages ?? [];
        const mailboxMessages = emailThread?.mailboxMessages ?? [];
        const oldestSent = sentMessages[sentMessages.length - 1];
        const oldestReceived = mailboxMessages[mailboxMessages.length - 1];
        const subject = oldestSent?.subject ?? oldestReceived?.subject ?? "(no subject)";
        const preview = pickEmailThreadPreview(sentMessages, mailboxMessages);
        const messageCount = sentMessages.length + mailboxMessages.length;
        const hasAttachments = mailboxMessages.some(m => m.attachmentCount > 0);
        const crossLink = emailThread
            ? getEmailCrossEntityLink(
                  emailThread,
                  entityTypeFromMode(mode),
                  "min-w-0 text-xs",
              )
            : null;
        const showEntity = mode.kind === "inbox";
        const hitWithName = showEntity
            ? pickMostRelevantHitWithName(thread.contactSearchHits)
            : null;
        return (
            <li className={rowClass} onClick={onSelect}>
                <EnvelopeIcon
                    className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-1"
                    aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm text-gray-900 truncate">
                            {subject}
                        </span>
                        {messageCount > 1 ? (
                            <span className="text-[10px] tabular-nums font-medium text-gray-600 bg-gray-100 rounded-full px-1.5 py-[1px] flex-shrink-0">
                                {messageCount}
                            </span>
                        ) : null}
                        {hasAttachments ? (
                            <PaperClipIcon
                                className="h-3 w-3 text-gray-400 flex-shrink-0"
                                aria-label="Has attachments"
                            />
                        ) : null}
                        <span className="text-[11px] tabular-nums text-gray-400 ml-auto flex-shrink-0">
                            {formatDateNoTime(thread.latestTimestamp)}
                        </span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">{preview}</div>
                    {hitWithName ? (
                        <div className="mt-1.5 flex items-center gap-2 min-w-0">
                            <EntityPill type={hitWithName.hit.type} />
                            <span className="text-[11px] text-gray-400 truncate">
                                {hitWithName.name}
                            </span>
                        </div>
                    ) : crossLink ? (
                        <span
                            className="mt-1.5 inline-flex max-w-full min-w-0"
                            onClick={e => e.stopPropagation()}>
                            {crossLink}
                        </span>
                    ) : null}
                </div>
            </li>
        );
    }

    if (thread.type === ConversationThreadType.VoiceCall && thread.voiceCall) {
        const voiceCall = thread.voiceCall;
        const isInbound = voiceCall.direction === VoiceCallDirection.Inbound;
        const remoteNumber = isInbound ? voiceCall.from : voiceCall.to;
        const showEntity = mode.kind === "inbox";
        const hitWithName = showEntity
            ? pickMostRelevantHitWithName(thread.contactSearchHits)
            : null;
        return (
            <li className={rowClass} onClick={onSelect}>
                <PhoneIcon
                    className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-1 ${
                        isInbound ? "transform scale-x-[-1]" : ""
                    }`}
                    aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm text-gray-900 truncate">
                            {formatPhoneNumber(remoteNumber)}
                        </span>
                        <span className="text-[11px] tabular-nums text-gray-400 ml-auto flex-shrink-0">
                            {formatDateNoTime(voiceCall.dateAdded)}
                        </span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                        {voiceCallStatusPreview(voiceCall)}
                    </div>
                    {hitWithName ? (
                        <div className="mt-1.5 flex items-center gap-2 min-w-0">
                            <EntityPill type={hitWithName.hit.type} />
                            <span className="text-[11px] text-gray-400 truncate">
                                {hitWithName.name}
                            </span>
                        </div>
                    ) : null}
                </div>
            </li>
        );
    }

    return null;
};

type OwnerBadgePlacement = "header" | "toolbar";

interface ThreadReadPaneProps {
    thread: Thread;
    mode: ConversationFeedMode;
    ownerBadgePlacement: OwnerBadgePlacement;
}

const ThreadReadPane: React.FC<ThreadReadPaneProps> = props => {
    const { thread, mode, ownerBadgePlacement } = props;
    const entityId = entityIdFromMode(mode);
    const entityType = entityTypeFromMode(mode);
    if (
        thread.type === ConversationThreadType.LeadSourceEmail &&
        thread.leadSourceEmail
    ) {
        return <LeadSourceEmailReadPane email={thread.leadSourceEmail} />;
    }
    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <ThreadReadPaneHeader
                thread={thread}
                mode={mode}
                ownerBadgePlacement={ownerBadgePlacement}
            />
            <div className="flex-1 min-h-0 flex flex-col">
                <ThreadReadPaneBody
                    thread={thread}
                    entityId={entityId}
                    entityType={entityType}
                />
            </div>
        </div>
    );
};

interface ThreadReadPaneBodyProps {
    thread: Thread;
    entityId: EmailEntityId | undefined;
    entityType: AdvancedSearchEntityType | undefined;
}

const ThreadReadPaneBody: React.FC<ThreadReadPaneBodyProps> = props => {
    const { thread, entityId, entityType } = props;
    const currentUserId = useCurrentUserId();

    if (thread.type === ConversationThreadType.Sms) {
        const messages = thread.smsThread?.messages ?? [];
        const latest = messages[0];
        const phoneLine = thread.smsThread?.phoneLine;
        if (!latest || !phoneLine) {
            return null;
        }
        const isInbound = latest.direction === "Inbound";
        const phoneNumber = isInbound ? latest.from : latest.to;
        const isOwnThread = phoneLine.userId === currentUserId;
        const senderName = latest.createdByUser?.displayName ?? "Unknown";

        return (
            <SmsMessageThreadInline
                phoneLineId={phoneLine.id}
                phoneNumber={phoneNumber}
                senderName={senderName}
                isOwnThread={isOwnThread}
            />
        );
    }

    if (thread.type === ConversationThreadType.EmailThread) {
        const emailEntity =
            entityId && entityType
                ? { entityId, entityType }
                : deriveEmailEntityFromHits(thread.contactSearchHits);
        return (
            <ul role="list" className="flex-1 min-h-0 overflow-y-auto py-2">
                <EmailThreadComponentInline
                    thread={thread}
                    entityId={emailEntity?.entityId}
                    entityType={emailEntity?.entityType}
                />
            </ul>
        );
    }

    if (thread.type === ConversationThreadType.VoiceCall && thread.voiceCall) {
        const remoteContactName =
            pickMostRelevantHitWithName(thread.contactSearchHits)?.name ?? null;
        return (
            <VoiceCallDetailInline
                voiceCall={thread.voiceCall}
                remoteContactName={remoteContactName}
            />
        );
    }

    return null;
};

interface ThreadReadPaneHeaderProps {
    thread: Thread;
    mode: ConversationFeedMode;
    ownerBadgePlacement: OwnerBadgePlacement;
}

const ThreadReadPaneHeader: React.FC<ThreadReadPaneHeaderProps> = props => {
    const { thread, mode, ownerBadgePlacement } = props;
    const entityType = entityTypeFromMode(mode);
    const namedHit =
        mode.kind === "inbox"
            ? pickMostRelevantHitWithName(thread.contactSearchHits)
            : null;

    if (thread.type === ConversationThreadType.Sms) {
        const messages = thread.smsThread?.messages ?? [];
        const latest = messages[0];
        if (!latest) {
            return null;
        }
        const phoneNumber = latest.direction === "Inbound" ? latest.from : latest.to;
        const title = namedHit?.name ?? formatPhoneNumber(phoneNumber);
        return (
            <header className="flex-none border-b border-gray-200 bg-white px-5 py-3.5">
                <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-medium text-gray-500 min-w-0">
                        <ChatBubbleLeftIcon
                            className="h-3 w-3 flex-shrink-0"
                            aria-hidden="true"
                        />
                        <span className="truncate">SMS</span>
                        {namedHit ? (
                            <>
                                <span aria-hidden="true" className="text-gray-300">
                                    ·
                                </span>
                                <span className="truncate">
                                    {formatPhoneNumber(phoneNumber)}
                                </span>
                            </>
                        ) : null}
                    </div>
                    <ReadPaneHeaderActions
                        ownerUserId={thread.ownerUserId}
                        namedHit={namedHit}
                        ownerBadgePlacement={ownerBadgePlacement}
                    />
                </div>
                <h2 className="text-base font-semibold text-gray-900 leading-snug truncate">
                    {title}
                </h2>
            </header>
        );
    }

    if (thread.type === ConversationThreadType.EmailThread) {
        const emailThread = thread.emailThread;
        if (!emailThread) {
            return null;
        }
        const sentMessages = emailThread.sentMessages ?? [];
        const mailboxMessages = emailThread.mailboxMessages ?? [];
        const oldestSent = sentMessages[sentMessages.length - 1];
        const oldestReceived = mailboxMessages[mailboxMessages.length - 1];
        const subject = oldestSent?.subject ?? oldestReceived?.subject ?? "(no subject)";
        const messageCount = sentMessages.length + mailboxMessages.length;
        const participants = getEmailParticipantCaption(emailThread);
        const crossLink = getEmailCrossEntityLink(
            emailThread,
            entityType,
            "flex-shrink-0 text-xs",
        );
        return (
            <header className="flex-none border-b border-gray-200 bg-white px-5 py-3.5">
                <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-medium text-gray-500 min-w-0">
                        <EnvelopeIcon
                            className="h-3 w-3 flex-shrink-0"
                            aria-hidden="true"
                        />
                        <span className="truncate">Email</span>
                        {messageCount > 1 ? (
                            <>
                                <span aria-hidden="true" className="text-gray-300">
                                    ·
                                </span>
                                <span className="tabular-nums">
                                    {messageCount} messages
                                </span>
                            </>
                        ) : null}
                    </div>
                    <ReadPaneHeaderActions
                        ownerUserId={thread.ownerUserId}
                        namedHit={namedHit}
                        ownerBadgePlacement={ownerBadgePlacement}
                    />
                </div>
                <h2 className="text-base font-semibold text-gray-900 leading-snug truncate">
                    {subject}
                </h2>
                {(participants || crossLink) && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 min-w-0">
                        {participants ? (
                            namedHit ? (
                                <Link
                                    to={namedHit.hit.appLink}
                                    className="truncate text-brand-600 hover:text-brand-800 hover:underline">
                                    with {participants}
                                </Link>
                            ) : (
                                <span className="truncate">with {participants}</span>
                            )
                        ) : null}
                        {crossLink}
                    </div>
                )}
            </header>
        );
    }

    if (thread.type === ConversationThreadType.VoiceCall && thread.voiceCall) {
        const voiceCall = thread.voiceCall;
        const isInbound = voiceCall.direction === VoiceCallDirection.Inbound;
        const remoteNumber = isInbound ? voiceCall.from : voiceCall.to;
        const directionLabel = isInbound ? "inbound" : "outbound";
        const title = namedHit?.name ?? formatPhoneNumber(remoteNumber);
        return (
            <header className="flex-none border-b border-gray-200 bg-white px-5 py-3.5">
                <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-medium text-gray-500 min-w-0">
                        <PhoneIcon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                        <span className="truncate">Call</span>
                        <span aria-hidden="true" className="text-gray-300">
                            ·
                        </span>
                        <span>{directionLabel}</span>
                        {voiceCall.recordingDurationSec ? (
                            <>
                                <span aria-hidden="true" className="text-gray-300">
                                    ·
                                </span>
                                <span className="tabular-nums">
                                    {formatCallDuration(voiceCall.recordingDurationSec)}
                                </span>
                            </>
                        ) : null}
                        {namedHit ? (
                            <>
                                <span aria-hidden="true" className="text-gray-300">
                                    ·
                                </span>
                                <span className="truncate">
                                    {formatPhoneNumber(remoteNumber)}
                                </span>
                            </>
                        ) : null}
                    </div>
                    <ReadPaneHeaderActions
                        ownerUserId={thread.ownerUserId}
                        namedHit={namedHit}
                        ownerBadgePlacement={ownerBadgePlacement}
                    />
                </div>
                <h2 className="text-base font-semibold text-gray-900 leading-snug truncate">
                    {title}
                </h2>
            </header>
        );
    }

    return null;
};

type EmailThread = NonNullable<Thread["emailThread"]>;

function getEmailParticipantCaption(emailThread: EmailThread): string | null {
    const mailboxMessages = emailThread.mailboxMessages ?? [];
    const sentMessages = emailThread.sentMessages ?? [];

    const latestInbound = mailboxMessages[0];
    if (latestInbound?.from) {
        const display =
            latestInbound.from.resolvedName ??
            latestInbound.from.name ??
            latestInbound.from.email ??
            null;
        if (display) {
            return display;
        }
    }

    const latestOutbound = sentMessages[0];
    const recipients = latestOutbound?.to ?? [];
    if (recipients.length > 0) {
        return recipients[0];
    }

    return null;
}

function getEmailCrossEntityLink(
    emailThread: EmailThread,
    entityType: AdvancedSearchEntityType | undefined,
    className: string,
): React.ReactNode {
    const sentMessages = emailThread.sentMessages ?? [];
    for (const sent of sentMessages) {
        if (entityType === AdvancedSearchEntityType.Client && sent.intakeCommunity) {
            return (
                <CommunityLink
                    communityId={sent.intakeCommunity.id}
                    name={sent.intakeCommunity.name ?? "(no name)"}
                    className={className}
                />
            );
        }
        if (entityType === AdvancedSearchEntityType.Community && sent.intakeClient) {
            const client = sent.intakeClient;
            return (
                <ClientLink
                    clientId={client.id}
                    name={client.name ?? client.bestContactName ?? "(no name)"}
                    className={className}
                />
            );
        }
    }
    return null;
}

function useViewportFitHeight(ref: React.RefObject<HTMLElement>): void {
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) {
            return undefined;
        }
        const update = (): void => {
            const top = Math.max(el.getBoundingClientRect().top, 0);
            el.style.height = `calc(100vh - ${top + BOTTOM_GAP_PX}px)`;
        };
        update();
        let raf = 0;
        const onChange = (): void => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(update);
        };
        window.addEventListener("scroll", onChange, { passive: true });
        window.addEventListener("resize", onChange);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("scroll", onChange);
            window.removeEventListener("resize", onChange);
        };
    }, [ref]);
}

const hitTypeRelevance: Record<ContactSearchHitType, number> = {
    [ContactSearchHitType.Client]: 100,
    [ContactSearchHitType.BestContact]: 90,
    [ContactSearchHitType.ClientContact]: 80,
    [ContactSearchHitType.CommunityContact]: 70,
    [ContactSearchHitType.ReferenceContact]: 60,
    [ContactSearchHitType.Community]: 50,
    [ContactSearchHitType.ReferenceBusiness]: 40,
};

interface NamedHit {
    hit: ContactSearchHitFragmentFragment;
    name: string;
}

function pickMostRelevantHitWithName(
    hits: ContactSearchHitFragmentFragment[],
): NamedHit | null {
    const named = hits
        .map(hit => {
            const name = getEntityDisplayName(hit);
            return name ? { hit, name } : null;
        })
        .filter((entry): entry is NamedHit => entry !== null);
    if (named.length === 0) {
        return null;
    }
    return named.sort(
        (a, b) => hitTypeRelevance[b.hit.type] - hitTypeRelevance[a.hit.type],
    )[0];
}

function deriveEmailEntityFromHits(
    hits: ContactSearchHitFragmentFragment[],
): EmailEntityRef | null {
    const best = pickMostRelevantHitWithName(hits);
    return best ? emailEntityRefFromHit(best.hit) : null;
}

const entityPillStyle: Record<ContactSearchHitType, { label: string; cls: string }> = {
    [ContactSearchHitType.Client]: {
        label: "Client",
        cls: "bg-indigo-50 text-indigo-700 border-indigo-200",
    },
    [ContactSearchHitType.BestContact]: {
        label: "Client",
        cls: "bg-indigo-50 text-indigo-700 border-indigo-200",
    },
    [ContactSearchHitType.ClientContact]: {
        label: "Client",
        cls: "bg-indigo-50 text-indigo-700 border-indigo-200",
    },
    [ContactSearchHitType.Community]: {
        label: "Community",
        cls: "bg-teal-50 text-teal-700 border-teal-200",
    },
    [ContactSearchHitType.CommunityContact]: {
        label: "Community",
        cls: "bg-teal-50 text-teal-700 border-teal-200",
    },
    [ContactSearchHitType.ReferenceContact]: {
        label: "Referral Contact",
        cls: "bg-amber-50 text-amber-800 border-amber-200",
    },
    [ContactSearchHitType.ReferenceBusiness]: {
        label: "Referral Source",
        cls: "bg-amber-50 text-amber-800 border-amber-200",
    },
};

interface EntityPillProps {
    type: ContactSearchHitType;
}

const EntityPill: React.FC<EntityPillProps> = props => {
    const style = entityPillStyle[props.type];
    return (
        <span
            className={`inline-flex items-center text-[10.5px] font-medium border rounded px-1.5 py-0.5 flex-shrink-0 ${style.cls}`}>
            {style.label}
        </span>
    );
};

const baseChannelPillOptions: ButtonSelectOption<MessageChannelFilter>[] = [
    { id: "all", label: "All" },
    { id: "email", label: "Email", icon: EnvelopeIcon },
    { id: "sms", label: "SMS", icon: ChatBubbleLeftIcon },
];

const voiceChannelPillOption: ButtonSelectOption<MessageChannelFilter> = {
    id: "voice",
    label: "Voice",
    icon: PhoneIcon,
};

interface ChannelFilterPillsProps {
    active: MessageChannelFilter;
    onChange: (filter: MessageChannelFilter) => void;
    showVoice: boolean;
    counts: Record<MessageChannelFilter, number>;
}

const ChannelFilterPills: React.FC<ChannelFilterPillsProps> = props => {
    const { active, onChange, showVoice, counts } = props;
    const visibleOptions = showVoice
        ? [...baseChannelPillOptions, voiceChannelPillOption]
        : baseChannelPillOptions;
    const options = visibleOptions.map(option => ({
        ...option,
        count: counts[option.id],
    }));
    return <ButtonSelect options={options} selectedIds={[active]} onToggle={onChange} />;
};

const channelFilterEmptyMessage: Record<MessageChannelFilter, string> = {
    all: "No messages yet",
    email: "No email conversations",
    sms: "No text conversations",
    voice: "No calls",
};

const readPanePlaceholderMessage: Record<MessageChannelFilter, string> = {
    all: "Select a conversation to read",
    email: "Select a conversation to read",
    sms: "Select a conversation to read",
    voice: "Select a call",
};

interface ChannelFilterEmptyStateProps {
    filter: MessageChannelFilter;
}

const ChannelFilterEmptyState: React.FC<ChannelFilterEmptyStateProps> = props => (
    <div className="flex flex-col items-center text-center py-12 gap-3 text-gray-400">
        <InboxIcon className="h-10 w-10" aria-hidden="true" />
        <p className="text-sm">{channelFilterEmptyMessage[props.filter]}</p>
    </div>
);

interface ViaBadgeProps {
    ownerUserId: UserId | null | undefined;
}

const ViaBadge: React.FC<ViaBadgeProps> = props => {
    const usersState = useUsers();
    if (!props.ownerUserId) {
        return null;
    }
    const name = usersState.users[props.ownerUserId]?.name;
    if (!name) {
        return null;
    }
    return (
        <span className="inline-flex items-center gap-1 min-w-0 flex-shrink-0">
            <span className="text-[10.5px] text-gray-400" aria-hidden="true">
                via
            </span>
            <span className="inline-flex items-center text-[10.5px] font-medium border rounded px-1.5 py-0.5 bg-slate-100 text-slate-700 border-slate-200">
                {name}
            </span>
        </span>
    );
};

interface OpenEntityButtonProps {
    hit: ContactSearchHitFragmentFragment;
}

const OpenEntityButton: React.FC<OpenEntityButtonProps> = props => {
    const history = useHistory();
    const label = entityPillStyle[props.hit.type].label.toLowerCase();
    return (
        <SecondaryCompactButton
            type="button"
            className="flex-shrink-0 whitespace-nowrap"
            onClick={() => history.push(props.hit.appLink)}>
            Open {label}
        </SecondaryCompactButton>
    );
};

interface ReadPaneHeaderActionsProps {
    ownerUserId: UserId | null | undefined;
    namedHit: NamedHit | null;
    ownerBadgePlacement: OwnerBadgePlacement;
}

const ReadPaneHeaderActions: React.FC<ReadPaneHeaderActionsProps> = props => {
    const { ownerUserId, namedHit, ownerBadgePlacement } = props;
    const viaBadge =
        ownerBadgePlacement === "header" ? <ViaBadge ownerUserId={ownerUserId} /> : null;
    if (!namedHit) {
        return viaBadge;
    }
    return (
        <div className="flex items-center gap-2 flex-shrink-0">
            <OpenEntityButton hit={namedHit.hit} />
            {viaBadge}
        </div>
    );
};
